// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Post-login onboarding  (/welcome/profile → /welcome/terms → delivery → consent → aboard)
// Step 3 of 5 — "Legal & Freshness Charter": a scroll-to-read summary of the Terms of Service,
// Privacy Policy and Disclaimer (content: data/charter.ts, drawn from the published documents).
// The checkbox and the Continue button unlock only after the customer has scrolled to the end and
// ticked the box; accepting records one log row per document on the server (POST /api/me/consents).
// Reachable only right after a login (`fromLogin` state); a customer who has already accepted the
// current version is passed straight on.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowDown, ArrowLeft, ArrowRight, CircleAlert, CircleCheck, ExternalLink, Headset, History, Loader2, Lock, ScrollText } from "lucide-react";
import PageTransition from "../components/PageTransition";
import { CHARTER_INTRO, CHARTER_SECTIONS } from "../data/charter";
import { EFFECTIVE_DATE } from "../data/privacyPolicy";
import { SUPPORT_LINK } from "../data/welcome";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import { ApiError } from "../utils/api";
import { POLICY_VERSION, acceptPolicies, fetchConsentState } from "../utils/policy";

/** How close to the end (px) still counts as "reached the bottom" — sub-pixel scroll rounding. */
const BOTTOM_TOLERANCE = 12;

/** What to tell the customer when recording acceptance fails (the cause matters: only some are retryable). */
function acceptanceError(caught: unknown): string {
  if (caught instanceof ApiError) {
    if (caught.code === "POLICY_VERSION_MISMATCH") return "The terms were just updated. Please reload this page and read the latest version.";
    if (caught.status === 401) return "We couldn't verify your sign-in with our server. Please sign out, log in again and retry.";
    if (caught.status === 503) return "Our server is busy. Please try again in a moment.";
  }
  return "We couldn't record your acceptance. Check your connection and try again.";
}

export default function Charter() {
  useDocumentTitle("Terms & privacy");
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const reducedMotion = usePrefersReducedMotion();
  const arrivedFromLogin = (location.state as { fromLogin?: boolean } | null)?.fromLogin === true;
  const signedIn = auth.status === "signed-in";

  const scroller = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  // The acceptance version the server currently wants (staff can move it forward); the built-in one until we hear.
  const [version, setVersion] = useState(POLICY_VERSION);

  const onward = useCallback(
    () => navigate("/welcome/delivery", { state: { fromLogin: true }, replace: true }),
    [navigate],
  );

  // Already accepted this version (e.g. a returning customer)? Nothing to ask.
  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    fetchConsentState().then(
      (state) => {
        if (cancelled) return;
        setVersion(state.currentVersion);
        if (state.upToDate) onward();
        else setChecking(false);
      },
      () => !cancelled && setChecking(false), // can't tell: show the charter rather than skip it
    );
    return () => {
      cancelled = true;
    };
  }, [signedIn, onward]);

  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    // Content that fits without scrolling counts as read.
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_TOLERANCE);
  }, []);

  useEffect(() => {
    if (checking) return;
    measure();
    const el = scroller.current;
    if (!el) return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checking, measure]);

  if (!arrivedFromLogin || auth.status === "signed-out") return <Navigate to="/" replace />;

  const scrollToEnd = () => {
    const el = scroller.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: reducedMotion ? "auto" : "smooth" });
    el?.focus({ preventScroll: true }); // a plain focus() would cancel the smooth scroll
  };

  const accept = async () => {
    if (saving || !atBottom || !agreed) return;
    setSaving(true);
    setError(null);
    try {
      await acceptPolicies(version);
      onward();
    } catch (caught) {
      setSaving(false);
      setError(acceptanceError(caught));
    }
  };

  const ready = atBottom && agreed;
  const buttonLabel = saving ? "Saving…" : !atBottom ? "Read to the bottom to continue" : !agreed ? "Tick the box to continue" : "Accept & continue";

  return (
    <PageTransition>
      <div className="min-h-screen bg-foam-50 pt-8 text-ocean-950 dark:bg-ocean-950 dark:text-foam-50">
        <header className="mx-auto flex max-w-xl items-center justify-between px-4 pt-4">
          <button
            type="button"
            aria-label="Go back"
            onClick={() => navigate("/welcome/profile", { state: { fromLogin: true } })}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-ocean-700/10 hover:bg-ocean-700/15 focus-visible:outline-2 focus-visible:outline-teal-600"
          >
            <ArrowLeft className="h-6 w-6" aria-hidden="true" />
          </button>
          <a
            href={SUPPORT_LINK}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-11 items-center gap-2 rounded-full bg-ocean-700/10 px-4 text-sm font-bold hover:bg-ocean-700/15 focus-visible:outline-2 focus-visible:outline-teal-600"
          >
            <Headset className="h-5 w-5" aria-hidden="true" />
            Need help?
            <span className="sr-only"> (opens WhatsApp in a new tab)</span>
          </a>
        </header>

        <main className="mx-auto flex max-w-xl flex-col gap-4 px-4 pb-8 pt-4">
          <div className="text-center">
            <img src="/logo.png" alt="" className="mx-auto h-16 w-16 object-contain" />
            <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-ocean-700/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-ocean-800 dark:bg-white/10 dark:text-foam-100">
              <ScrollText className="h-4 w-4" aria-hidden="true" />
              Step 3 of 5 • Transparency
            </p>
            <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight">Legal &amp; Freshness Charter</h1>
            <p className="mx-auto mt-1.5 max-w-sm text-sm text-ocean-800/85 dark:text-foam-100/80">{CHARTER_INTRO}</p>
            <p className="mt-2 flex flex-wrap items-center justify-center gap-x-3 text-xs text-ocean-800/80 dark:text-foam-100/70">
              <span className="inline-flex items-center gap-1">
                <History className="h-3.5 w-3.5" aria-hidden="true" />
                Version {version} · effective {EFFECTIVE_DATE}
              </span>
            </p>
          </div>

          {checking ? (
            <p role="status" className="mt-10 flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
              Checking your acceptance…
            </p>
          ) : (
            <>
              <div className="relative">
                <div
                  ref={scroller}
                  onScroll={measure}
                  tabIndex={0}
                  role="region"
                  aria-label="Terms of Service, Privacy Policy and Disclaimer summary. Scroll to the end to enable acceptance."
                  className="h-[48vh] min-h-72 overflow-y-auto rounded-2xl bg-white p-5 shadow-lg shadow-ocean-900/10 outline-none focus-visible:ring-2 focus-visible:ring-teal-600 dark:bg-ocean-900"
                >
                  {CHARTER_SECTIONS.map((section, index) => (
                    <section key={section.id} aria-labelledby={`charter-${section.id}`} className={index > 0 ? "mt-8" : ""}>
                      <h2 id={`charter-${section.id}`} className="flex items-center gap-3 font-display text-xl font-bold text-ocean-800 dark:text-teal-300">
                        <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ocean-700/10 text-sm dark:bg-white/10">
                          {index + 1}
                        </span>
                        {section.title}
                      </h2>
                      <p className="mt-2 text-sm text-ocean-800/85 dark:text-foam-100/80">{section.intro}</p>
                      <ul className="mt-3 flex flex-col gap-3">
                        {section.points.map((point) => (
                          <li key={point.heading} className="rounded-xl bg-ocean-700/5 p-4 dark:bg-white/10">
                            <h3 className="text-sm font-bold">{point.heading}</h3>
                            <p className="mt-1 text-sm leading-relaxed text-ocean-800/90 dark:text-foam-100/85">{point.body}</p>
                            <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-wide text-ocean-800/60 dark:text-foam-100/60">{point.clause}</p>
                          </li>
                        ))}
                      </ul>
                      <Link
                        to={section.fullDocument.to}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-teal-700 underline-offset-2 hover:underline dark:text-teal-300"
                      >
                        {section.fullDocument.label}
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                        <span className="sr-only"> (opens in a new tab)</span>
                      </Link>
                    </section>
                  ))}
                  <p className="mt-8 border-t border-ocean-900/10 pt-4 text-center text-xs font-semibold text-ocean-800/70 dark:border-white/10 dark:text-foam-100/70">
                    — End of summary —
                  </p>
                </div>
                {!atBottom && (
                  <button
                    type="button"
                    onClick={scrollToEnd}
                    className="absolute bottom-4 right-4 inline-flex min-h-11 items-center gap-2 rounded-full bg-ocean-800 px-4 text-sm font-bold text-white shadow-lg hover:bg-ocean-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
                  >
                    <ArrowDown className="h-4 w-4" aria-hidden="true" />
                    Read to bottom
                  </button>
                )}
              </div>

              <p
                role="status"
                className={`flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${
                  atBottom ? "bg-teal-100 text-teal-700" : "bg-ocean-700/10 text-ocean-800 dark:bg-white/10 dark:text-foam-100"
                }`}
              >
                {atBottom ? <CircleCheck className="h-4 w-4" aria-hidden="true" /> : <ArrowDown className="h-4 w-4" aria-hidden="true" />}
                {atBottom ? "You've reached the end — you can accept now" : "Please scroll down to read all terms before accepting"}
              </p>

              <label className={`flex items-start gap-3 rounded-xl p-3 text-sm ${atBottom ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}>
                <input
                  id="charter-agree"
                  type="checkbox"
                  checked={agreed}
                  disabled={!atBottom}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 h-6 w-6 shrink-0 accent-teal-700"
                />
                <span>
                  I have read and agree to ZenFresh&apos;s <strong>Terms of Service</strong>, <strong>Privacy Policy</strong> and{" "}
                  <strong>Food Safety &amp; Availability Disclaimer</strong>.
                </span>
              </label>

              <p role="alert" className="flex items-center justify-center gap-1.5 text-center text-sm text-coral-600 dark:text-coral-300">
                {error && <CircleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />}
                {error}
              </p>

              <button
                type="button"
                onClick={accept}
                aria-disabled={!ready || saving}
                className={`flex min-h-14 w-full items-center justify-center gap-2 rounded-full text-base font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
                  ready && !saving
                    ? "bg-ocean-800 text-white shadow-md hover:bg-ocean-700"
                    : "cursor-not-allowed bg-ocean-700/10 text-ocean-800/70 dark:bg-white/10 dark:text-foam-100/70"
                }`}
              >
                {saving ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : !ready ? <Lock className="h-5 w-5" aria-hidden="true" /> : null}
                {buttonLabel}
                {ready && !saving && <ArrowRight className="h-5 w-5" aria-hidden="true" />}
              </button>
              <p className="flex items-center justify-center gap-1.5 text-center text-xs text-ocean-800/75 dark:text-foam-100/70">
                <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                Your acceptance is recorded with the version and the time.
              </p>
            </>
          )}
        </main>
      </div>
    </PageTransition>
  );
}
